import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { api } from '../api';
import { Search, Plus, Trash2, TrendingUp, MessageCircle, BarChart3, ChevronDown, ChevronUp, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';

const SearchInsightsPage = () => {
  const [groupedData, setGroupedData] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [newPrompt, setNewPrompt] = useState('');
  const [newPromptMode, setNewPromptMode] = useState('general');
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});

  const fetchPrompts = async () => {
    try {
      const res = await api.get('/analytics/prompts');
      setPrompts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGroupedQueries = async () => {
    setLoadingGroups(true);
    try {
      const res = await api.get('/analytics/queries/grouped?limit=20');
      setGroupedData(res.data);
    } catch (err) {
      console.error('Failed to fetch grouped queries:', err);
      toast.error('Failed to load search insights');
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
    fetchGroupedQueries();
  }, []);

  const handleAddPrompt = async (e) => {
    e.preventDefault();
    if (!newPrompt.trim()) return;

    try {
      await api.post('/analytics/prompts', { 
        text: newPrompt,
        mode: newPromptMode,
        category: 'Custom'
      });
      toast.success('Prompt added');
      setNewPrompt('');
      fetchPrompts();
    } catch (err) {
      toast.error('Failed to add prompt');
    }
  };

  const handleDeletePrompt = async (id) => {
    try {
      await api.delete(`/analytics/prompts/${id}`);
      toast.success('Prompt removed');
      fetchPrompts();
    } catch (err) {
      toast.error('Failed to delete prompt');
    }
  };

  const toggleGroup = (idx) => {
    setExpandedGroups(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const groups = groupedData?.groups || [];
  const totalQueries = groupedData?.total_queries || 0;
  const maxCount = groups.length > 0 ? groups[0].count : 1;

  return (
    <div data-testid="search-insights-page" className="h-full overflow-auto p-6 md:p-10 max-w-7xl mx-auto space-y-8 rounded-3xl lg-glass">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold tracking-tight text-white">Search Insights</h1>
        <p className="text-white/50">Track user questions and curate home screen topics.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Curated Prompts Section */}
        <Card data-testid="prompts-card" className="lg-glass-strong border-white/10 text-white lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-cyan-400" />
              Home Screen Prompts
            </CardTitle>
            <CardDescription className="text-white/40">Suggested questions for agents by mode.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleAddPrompt} className="space-y-3" data-testid="add-prompt-form">
              <div className="flex gap-2">
                <Select value={newPromptMode} onValueChange={setNewPromptMode}>
                  <SelectTrigger data-testid="prompt-mode-select" className="w-[140px] bg-black/20 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="lg-glass-strong border-white/10 text-white">
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="strategy">Deal Strategy</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="brokerops">BrokerOps</SelectItem>
                    <SelectItem value="market">Market Intel</SelectItem>
                  </SelectContent>
                </Select>
                <Input 
                  placeholder="Add new suggested question..." 
                  value={newPrompt}
                  onChange={(e) => setNewPrompt(e.target.value)}
                  data-testid="new-prompt-input"
                  className="bg-black/20 border-white/10 text-white placeholder:text-white/20 focus-visible:ring-cyan-500/50 flex-1"
                />
                <Button type="submit" size="icon" data-testid="add-prompt-btn" className="bg-cyan-500 text-black hover:bg-cyan-400 shrink-0">
                  <Plus className="h-5 w-5" />
                </Button>
              </div>
            </form>

            <div className="space-y-2 max-h-[400px] overflow-auto pr-2">
              {prompts.map((prompt) => (
                <div key={prompt.id} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/5 group">
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-white/80">{prompt.text}</span>
                    <Badge variant="outline" className="w-fit text-[10px] border-white/10 text-white/40 capitalize">
                      {prompt.mode || 'General'}
                    </Badge>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={() => handleDeletePrompt(prompt.id)}
                    data-testid={`delete-prompt-${prompt.id}`}
                    className="h-8 w-8 text-white/30 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* AI-Grouped Trending Searches */}
        <Card data-testid="grouped-searches-card" className="lg-glass-strong border-white/10 text-white lg:col-span-3 flex flex-col">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-cyan-400" />
                  Trending Searches
                  {totalQueries > 0 && (
                    <Badge variant="outline" className="ml-2 bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-[10px]">
                      {totalQueries} total
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-white/40 mt-1">
                  AI groups similar queries together and ranks by popularity.
                </CardDescription>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={fetchGroupedQueries}
                disabled={loadingGroups}
                data-testid="refresh-groups-btn"
                className="h-8 w-8 text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10"
              >
                <RefreshCw className={`h-4 w-4 ${loadingGroups ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-auto space-y-3">
            {loadingGroups ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/5">
                    <Skeleton className="h-5 w-3/4 bg-white/10 mb-2" />
                    <Skeleton className="h-3 w-1/2 bg-white/5" />
                  </div>
                ))}
              </div>
            ) : groups.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-white/30">
                <BarChart3 className="h-10 w-10 mb-3" />
                <p className="text-sm">No searches recorded yet.</p>
                <p className="text-xs text-white/20 mt-1">Queries will appear here as agents use the system.</p>
              </div>
            ) : (
              groups.map((group, idx) => (
                <Collapsible key={idx} open={expandedGroups[idx]} onOpenChange={() => toggleGroup(idx)}>
                  <CollapsibleTrigger asChild>
                    <div 
                      data-testid={`search-group-${idx}`}
                      className="w-full p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/[0.07] transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-sm font-medium text-white/90 truncate">{group.topic}</span>
                            <Badge className="bg-cyan-500/15 text-cyan-400 border-0 text-[11px] font-semibold shrink-0">
                              {group.count} {group.count === 1 ? 'query' : 'queries'}
                            </Badge>
                          </div>
                          {/* Popularity bar */}
                          <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-cyan-500/60 to-cyan-400/30 rounded-full transition-all duration-500"
                              style={{ width: `${Math.max((group.count / maxCount) * 100, 8)}%` }}
                            />
                          </div>
                          <p className="text-xs text-white/40 mt-2 truncate italic">
                            "{group.representative_query}"
                          </p>
                        </div>
                        <div className="shrink-0 text-white/30 mt-1">
                          {expandedGroups[idx] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </div>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-4 mt-1 space-y-1 pb-1">
                      {group.searches.map((search, sIdx) => (
                        <div key={sIdx} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.03]">
                          <Search className="h-3 w-3 text-white/20 shrink-0" />
                          <span className="text-xs text-white/60 flex-1 truncate">{search.question}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            {search.state && (
                              <Badge variant="outline" className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-[9px]">
                                {search.state}
                              </Badge>
                            )}
                            <span className="text-[10px] text-white/25">
                              {search.timestamp ? new Date(search.timestamp).toLocaleDateString() : ''}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SearchInsightsPage;
